import {
  ContainerService,
  ContainerStatusUpdate,
} from "@/app/services/generated/containerService";
import { EnumContainerStatus } from "../generated/enums/enumContainerStatus.generated";
import type Guid from "@/app/types/common/guid";
import messages from "@/plugins/i18n";
import { BrewManError } from "@/app/errors";

export default class ContainerServiceExtended extends ContainerService {
  getValidNewContainerStatuses(status: EnumContainerStatus) {
    let validStatuses: EnumContainerStatus[] = [
      EnumContainerStatus.fullAndIn,
      EnumContainerStatus.out,
    ];
    if (!status.isLost()) {
      validStatuses.push(EnumContainerStatus.lost);
    }
    if (!status.isCleanAndIn()) {
      validStatuses.push(EnumContainerStatus.cleanAndIn);
    }
    if (!status.isDirtyAndIn()) {
      validStatuses.push(EnumContainerStatus.dirtyAndIn);
    }
    if (!status.isOutOfOrder()) {
      validStatuses.push(EnumContainerStatus.outOfOrder);
    }
    return validStatuses;
  }

  async fillContainers(
    permLabels: readonly string[],
    productBatchId: Guid,
    assemblyId: Guid | null,
    processId: Guid | null
  ) {
    const containers = await this.getContainersByPermLabels(permLabels);

    if (!permLabels.every((pl) => containers.some((c) => c.permLabel === pl))) {
      // Missing container for a label
      throw new BrewManError(messages.containers_not_found);
    }

    const containerUpdates = containers.map((c) => {
      const newStatus = new ContainerStatusUpdate();
      newStatus.containerId = c.id;
      newStatus.productBatchId = productBatchId;
      newStatus.filledFromAssemblyId = assemblyId;
      newStatus.isIn = true;
      newStatus.filledFromProcessId = processId;

      return newStatus;
    });

    if (containerUpdates.length > 0) {
      await this.updateContainerStatuses(containerUpdates);
    }
  }

  async emptyContainers(permLabels: readonly string[]) {
    const containers = await this.getContainersByPermLabels(permLabels);

    if (!permLabels.every((pl) => containers.some((c) => c.permLabel === pl))) {
      // Missing container for a label
      throw new BrewManError(messages.containers_not_found);
    }

    const containerUpdates = containers.map((c) => {
      const newStatus = new ContainerStatusUpdate();
      newStatus.containerId = c.id;
      newStatus.productBatchId = null;
      newStatus.filledFromAssemblyId = null;
      newStatus.isIn = true;
      newStatus.needsCleaning = true;
      newStatus.filledFromProcessId = null;

      return newStatus;
    });

    if (containerUpdates.length > 0) {
      await this.updateContainerStatuses(containerUpdates);
    }
  }
}

export const containerService = new ContainerServiceExtended();
